import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button/Button";
import Icon from "@insite/mobius/Icon";
import CenteredSlide from "@midland-industries/Components/Slider/CenteredSlide";
import RightAlignedSlide from "@midland-industries/Components/Slider/RightAlignedSlide";
import ThreeByOneSlide from "@midland-industries/Components/Slider/ThreeByOneSlide";
import TwoByTwoSlide from "@midland-industries/Components/Slider/TwoByTwoSlide";
import IconChevronLeft from "@midland-industries/Icons/IconChevronLeft";
import IconChevronRight from "@midland-industries/Icons/IconChevronRight";
import { SlideshowStyles } from "@midland-industries/Overrides/Widgets/Basic/Slideshow";
import { colors } from "@midland-industries/Styles/colors";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useCallback, useEffect } from "react";
import { css } from "styled-components";

const slideshowStyles: SlideshowStyles = {
    imageStyles: {},
    prevArrowButton: {
        css: css`
            margin-left: 45px;
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowButton: {
        css: css`
            margin-right: 45px;
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
};

export interface SlideFields {
    slideTitle: string;
    layoutType: "threeByOne" | "twoByTwo" | "centeredWithBackground" | "rightWithBackground";
    background: "image" | "color";
    image: string;
    imageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
    backgroundColor: string;
    lightText: boolean;
    eyebrowText: string;
    headingText: string;
    contentText: string;
    contentImage: string;
    callToActionLabel: string;
    callToActionLink: LinkFieldValue;
}

interface SlideModel {
    fields: SlideFields;
}

const enum fields {
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
}

interface SliderProps extends WidgetProps {
    fields: {
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.slides]: SlideModel[];
    };
}

const Slider: React.FC<SliderProps> = ({ fields }) => {
    const [emblaRef, embla] = useEmblaCarousel();

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }
        const draggable = fields.slides.length > 1;

        embla.reInit({
            align: "start",
            loop: true,
            draggable,
        });
    }, [embla, fields.slides]);

    return (
        <div style={{ height: 471 }}>
            <div className="slideshow-wrapper" style={{ position: "absolute", left: 0, maxWidth: "100%" }}>
                {fields.slides.length > 1 ? (
                    <div style={{ position: "absolute", zIndex: 1, top: 122 }}>
                        <Button
                            {...slideshowStyles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            aria-label={translate("Previous")}
                        >
                            <Icon color={colors.textAccent} size={24} src={IconChevronLeft} />
                        </Button>
                    </div>
                ) : (
                    <></>
                )}
                <div
                    id="carousel"
                    ref={emblaRef}
                    className="slideshow-carousel"
                    style={{ overflow: "hidden", width: "100vw", maxWidth: "100%" }}
                >
                    <div className="slideshow-slide-container" style={{ display: "flex" }}>
                        {fields.slides.map((slide, index) => {
                            const { fields } = slide;
                            let background: React.CSSProperties = { background: fields.backgroundColor || "#FFFFFF" };

                            if (fields.background === "image") {
                                background = {
                                    backgroundImage: `url(${fields.image})`,
                                    backgroundSize: fields.imageBehavior || "cover",
                                };
                            }

                            return (
                                <div
                                    className="slide-wrapper"
                                    key={index}
                                    style={{
                                        height: 471,
                                        minWidth: "100%",
                                        position: "relative",
                                        ...background,
                                    }}
                                >
                                    {fields.layoutType === "threeByOne" ? <ThreeByOneSlide {...fields} /> : <></>}
                                    {fields.layoutType === "twoByTwo" ? <TwoByTwoSlide {...fields} /> : <></>}
                                    {fields.layoutType === "centeredWithBackground" ? (
                                        <CenteredSlide {...fields} />
                                    ) : (
                                        <></>
                                    )}
                                    {fields.layoutType === "rightWithBackground" ? (
                                        <RightAlignedSlide {...fields} />
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                {fields.slides.length > 1 ? (
                    <div style={{ position: "absolute", zIndex: 1, top: 122, right: 0 }}>
                        <Button
                            {...slideshowStyles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            aria-label={translate("Next")}
                        >
                            <Icon color={colors.textAccent} size={24} src={IconChevronRight} />
                        </Button>
                    </div>
                ) : (
                    <></>
                )}
            </div>
        </div>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const slider: WidgetModule = {
    component: Slider,
    definition: {
        displayName: "Slider",
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "layoutType",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Three By One", value: "threeByOne" },
                            { displayName: "Two By Two", value: "twoByTwo" },
                            {
                                displayName: "Centered Content",
                                value: "centeredWithBackground",
                            },
                            {
                                displayName: "Right Aligned Content",
                                value: "rightWithBackground",
                            },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "threeByOne",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            {
                                displayName: "Prioritize height",
                                value: "prioritizeHeight",
                            },
                            {
                                displayName: "Prioritize width",
                                value: "prioritizeWidth",
                            },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "lightText",
                        editorTemplate: "CheckboxField",
                        displayName: "Use Light Text",
                        defaultValue: false,
                    },
                    {
                        name: "eyebrowText",
                        editorTemplate: "TextField",
                        displayName: "Eyebrow Text",
                        defaultValue: "",
                    },
                    {
                        name: "headingText",
                        editorTemplate: "TextField",
                        displayName: "Heading Text",
                        defaultValue: "",
                    },
                    {
                        name: "contentText",
                        editorTemplate: "TextField",
                        displayName: "Content Text",
                        defaultValue: "",
                    },
                    {
                        name: "callToActionLabel",
                        editorTemplate: "TextField",
                        displayName: "Call To Action Label",
                        defaultValue: "",
                    },
                    {
                        name: "callToActionLink",
                        displayName: "Call To Action Label",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "contentImage",
                        displayName: "Content Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget =>
                            widget.fields.layoutType === "threeByOne" || widget.fields.layoutType === "twoByTwo",
                    },
                ],
            },
        ],
    },
};

export default slider;
